import React, { useState } from 'react';
import { mergeClasses } from 'minimal-shared/utils';

import IconButton from '@mui/material/IconButton';
import { alpha, styled } from '@mui/material/styles';
import ListItemText from '@mui/material/ListItemText';

import { fileData } from './utils';
import { uploadClasses } from './classes';
import { MultiFilePreviewProps } from './types';
import { FileThumbnail } from './FileThumbnail';
import Iconify from '../../../components/Iconify';
import { fData } from '../../../utils/file/format-number';
import FilePreviewDialog from '../../forms/file-upload/FilePreviewDialog';

// ----------------------------------------------------------------------

export function MultiFilePreview({
  sx,
  onRemove,
  lastNode,
  thumbnail,
  slotProps,
  firstNode,
  files = [],
  className,
  ...other
}: MultiFilePreviewProps) {
  const [open, setOpen] = useState<boolean>(false);
  const [file] = useState<File>();

  return (
    <ListRoot
      thumbnail={thumbnail}
      className={mergeClasses([uploadClasses.uploadMultiPreview, className])}
      sx={sx}
      {...other}
    >
      {firstNode && <ItemNode thumbnail={thumbnail}>{firstNode}</ItemNode>}

      {files.map((file) => {
        const { name, size } = fileData(file);

        if (thumbnail) {
          return (
            <ItemThumbnail key={name}>
              <FileThumbnail
                tooltip
                imageView
                file={file}
                onRemove={() => onRemove?.(file)}
                sx={[
                  (theme) => ({
                    width: 80,
                    height: 80,
                    border: `solid 1px ${theme.palette.grey['500_16'] || alpha(theme.palette.grey[500], 0.16)}`,
                  }),
                ]}
                slotProps={{ icon: { sx: { width: 36, height: 36 } } }}
                {...slotProps?.thumbnail}
              />
            </ItemThumbnail>
          );
        }

        return (
          <ItemRow key={name}>
            <FileThumbnail file={file} {...slotProps?.thumbnail} />

            <ListItemText
              primary={name}
              secondary={fData(size)}
              slotProps={{
                primary: {
                  noWrap: true,
                  sx: { overflow: 'hidden', textOverflow: 'ellipsis' },
                },
                secondary: { sx: { typography: 'caption' } },
              }}
              sx={{ flex: 1, minWidth: 0 }}

              // TODO To view files, an external link is required so that Microsoft Viewer can access them.
              // onClick={() => handleOpen(file as unknown as File)}
            />

            {onRemove && (
              <IconButton size="small" onClick={() => onRemove(file)}>
                <Iconify width={16} icon="mingcute:close-line" />
              </IconButton>
            )}
          </ItemRow>
        );
      })}

      {lastNode && <ItemNode thumbnail={thumbnail}>{lastNode}</ItemNode>}

      <FilePreviewDialog open={open} setOpen={setOpen} file={file} />
    </ListRoot>
  );
}

// ----------------------------------------------------------------------

const ListRoot = styled('ul', {
  shouldForwardProp: (prop: string) => !['thumbnail', 'sx'].includes(prop),
})<Pick<MultiFilePreviewProps, 'thumbnail'>>(({ thumbnail, theme }) => ({
  display: 'flex',
  gap: theme.spacing(1),
  flexDirection: 'column',
  ...(thumbnail && { flexWrap: 'wrap', flexDirection: 'row' }),
}));

const ItemThumbnail = styled('li')(() => ({ display: 'inline-flex' }));

const ItemRow = styled('li')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  padding: theme.spacing(1, 1, 1, 1.5),
  borderRadius: theme.shape.borderRadius,
  border: `solid 1px ${theme.palette.grey['500_16'] || alpha(theme.palette.grey[500], 0.16)}`,
}));

const ItemNode = styled('li', {
  shouldForwardProp: (prop: string) => !['thumbnail', 'sx'].includes(prop),
})<Pick<MultiFilePreviewProps, 'thumbnail'>>(({ thumbnail }) => ({
  ...(thumbnail && { width: 'auto', display: 'inline-flex' }),
}));
